import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export const SEOProvider = ({ children }) => {
  return (
    <HelmetProvider>
      {children}
    </HelmetProvider>
  );
};

export const SEO = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLang = i18n.language || 'es';
  
  // Base URL
  const baseUrl = 'https://onbuild.es';
  const currentUrl = `${baseUrl}${location.pathname}`;
  
  // Language-specific meta
  const titles = {
    es: 'OnBuild — Todos los Pisos Sobre Plano en España',
    en: 'OnBuild — All Off-Plan Homes in Spain',
    ru: 'OnBuild — Все Квартиры на Этапе Строительства в Испании',
    de: 'OnBuild — Alle Neubauwohnungen in Spanien',
    fr: 'OnBuild — Tous les Logements Neufs en Espagne',
    ar: 'OnBuild — جميع المساكن قيد الإنشاء في إسبانيا',
    nl: 'OnBuild — Alle Nieuwbouw in Spanje',
    it: 'OnBuild — Tutte le Case in Costruzione in Spagna',
    ro: 'OnBuild — Toate Locuințele în Construcție din Spania',
    pl: 'OnBuild — Wszystkie Mieszkania w Budowie w Hiszpanii',
    uk: 'OnBuild — Усі Квартири на Етапі Будівництва в Іспанії'
  };
  
  const descriptions = {
    es: 'Descubre todas las viviendas sobre plano y en construcción en España. Directamente de promotores, sin intermediarios. Recibe notificaciones antes que nadie.',
    en: 'Discover all off-plan properties and new construction homes in Spain. Direct from developers, no intermediaries. Get notified about new projects first.',
    ru: 'Откройте для себя все квартиры на этапе строительства в Испании. Напрямую от застройщиков, без посредников. Получайте уведомления первыми.',
    de: 'Entdecken Sie alle Neubauwohnungen in Spanien. Direkt von Bauträgern, ohne Zwischenhändler. Erhalten Sie als Erste Benachrichtigungen.',
    fr: 'Découvrez tous les logements neufs en construction en Espagne. Directement des promoteurs, sans intermédiaires. Soyez informé en premier.',
    ar: 'اكتشف جميع المساكن قيد الإنشاء في إسبانيا. مباشرة من المطورين، بدون وسطاء. احصل على الإشعارات أولاً.',
    nl: 'Ontdek alle nieuwbouw in Spanje. Direct van ontwikkelaars, zonder tussenpersonen. Ontvang als eerste meldingen.',
    it: 'Scopri tutte le case in costruzione in Spagna. Direttamente dai costruttori, senza intermediari. Ricevi notifiche per primo.',
    ro: 'Descoperă toate locuințele în construcție din Spania. Direct de la dezvoltatori, fără intermediari. Primește notificări primul.',
    pl: 'Odkryj wszystkie mieszkania w budowie w Hiszpanii. Bezpośrednio od deweloperów, bez pośredników. Otrzymuj powiadomienia jako pierwszy.',
    uk: 'Відкрийте для себе всі квартири на етапі будівництва в Іспанії. Безпосередньо від забудовників, без посередників. Отримуйте сповіщення першими.'
  };
  
  const title = titles[currentLang] || titles.es;
  const description = descriptions[currentLang] || descriptions.es;
  
  // Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        "url": baseUrl,
        "name": "OnBuild",
        "description": "Off-plan homes and new construction properties in Spain",
        "publisher": {
          "@id": `${baseUrl}/#organization`
        },
        "inLanguage": currentLang
      },
      {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        "name": "OnBuild",
        "url": baseUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/img/logo.png`,
          "width": 250,
          "height": 60
        },
        "sameAs": []
      },
      {
        "@type": "WebPage",
        "@id": `${currentUrl}#webpage`,
        "url": currentUrl,
        "name": title,
        "description": description,
        "isPartOf": {
          "@id": `${baseUrl}/#website`
        },
        "inLanguage": currentLang,
        "potentialAction": [
          {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${baseUrl}/?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        ]
      }
    ]
  };
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph */}
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content={currentLang} />
      
      {/* Twitter */}
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      
      {/* Alternate Languages */}
      {Object.keys(titles).map(lang => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`${baseUrl}/${lang}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={baseUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

